import React, { useState, useEffect } from 'react';
import { Card, Row, Col, Statistic, Table, Tag, Button, notification, Timeline, Spin, Alert, Progress, Badge } from 'antd';
import { 
  GlobalOutlined as GlobeIcon, 
  ReloadOutlined as RefreshCwIcon,
  CheckCircleOutlined as CheckCircleIcon,
  ExclamationCircleOutlined as AlertTriangleIcon,
  CloseCircleOutlined as XCircleIcon,
  DownloadOutlined as DownloadIcon,
  DatabaseOutlined as DatabaseIcon,
  SafetyOutlined as ShieldIcon,
  RiseOutlined as TrendingUpIcon,
  ClockCircleOutlined as ClockIcon
} from '@ant-design/icons';
import { aiService } from '../../services/ai';

interface DataSource {
  id: string;
  source_name: string;
  source_type: string;
  status: 'active' | 'inactive' | 'error' | 'syncing';
  last_fetch: string;
  next_fetch: string;
  records_fetched: number;
  success_rate: number;
  reliability_score: number;
  data_quality: number;
  api_endpoint: string;
  fetch_frequency_hours: number;
}

const InternetLearningMonitor: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [learningSources, setLearningSources] = useState<any>(null);

  // Mock external data sources - GetMyHive Safety Intelligence odaklı gerçek güvenlik kaynakları
  const [dataSources] = useState<DataSource[]>([
    {
      id: '1',
      source_name: 'Emniyet Genel Müdürlüğü',
      source_type: 'police_intelligence',
      status: 'active',
      last_fetch: new Date(Date.now() - 1000 * 60 * 15).toISOString(), // 15 dakika önce
      next_fetch: new Date(Date.now() + 1000 * 60 * 45).toISOString(), // 45 dakika sonra
      records_fetched: 342,
      success_rate: 98.2,
      reliability_score: 0.98,
      data_quality: 0.95,
      api_endpoint: 'https://api.emniyet.gov.tr/safety-intelligence',
      fetch_frequency_hours: 1
    },
    {
      id: '2',
      source_name: 'AFAD Acil Durum Yönetimi',
      source_type: 'emergency_management',
      status: 'active',
      last_fetch: new Date(Date.now() - 1000 * 60 * 8).toISOString(), // 8 dakika önce
      next_fetch: new Date(Date.now() + 1000 * 60 * 22).toISOString(), // 22 dakika sonra
      records_fetched: 127,
      success_rate: 94.8,
      reliability_score: 0.92,
      data_quality: 0.96,
      api_endpoint: 'https://api.afad.gov.tr/emergency-data',
      fetch_frequency_hours: 0.5
    },
    {
      id: '3',
      source_name: '112 Acil Çağrı Merkezi',
      source_type: 'emergency_calls',
      status: 'processing',
      last_fetch: new Date(Date.now() - 1000 * 60 * 3).toISOString(), // 3 dakika önce
      next_fetch: new Date(Date.now() + 1000 * 60 * 27).toISOString(), // 27 dakika sonra
      records_fetched: 423,
      success_rate: 91.7,
      reliability_score: 0.89,
      data_quality: 0.93,
      api_endpoint: 'https://api.112.gov.tr/emergency-calls',
      fetch_frequency_hours: 0.25
    },
    {
      id: '4',
      source_name: 'Jandarma Genel Komutanlığı',
      source_type: 'military_intelligence',
      status: 'active',
      last_fetch: new Date(Date.now() - 1000 * 60 * 45).toISOString(), // 45 dakika önce
      next_fetch: new Date(Date.now() + 1000 * 60 * 15).toISOString(), // 15 dakika sonra
      records_fetched: 198,
      success_rate: 96.4,
      reliability_score: 0.96,
      data_quality: 0.94,
      api_endpoint: 'https://api.jandarma.gov.tr/security-intel',
      fetch_frequency_hours: 2
    },
    {
      id: '5',
      source_name: 'İl Emniyet Müdürlükleri',
      source_type: 'local_police',
      status: 'active',
      last_fetch: new Date(Date.now() - 1000 * 60 * 20).toISOString(), // 20 dakika önce
      next_fetch: new Date(Date.now() + 1000 * 60 * 40).toISOString(), // 40 dakika sonra
      records_fetched: 267,
      success_rate: 93.6,
      reliability_score: 0.88,
      data_quality: 0.91,
      api_endpoint: 'https://api.emniyet.gov.tr/local-reports',
      fetch_frequency_hours: 1
    },
    {
      id: '6',
      source_name: 'Sağlık Bakanlığı Acil Servis',
      source_type: 'health_emergency',
      status: 'active',
      last_fetch: new Date(Date.now() - 1000 * 60 * 12).toISOString(), // 12 dakika önce
      next_fetch: new Date(Date.now() + 1000 * 60 * 18).toISOString(), // 18 dakika sonra
      records_fetched: 89,
      success_rate: 97.1,
      reliability_score: 0.91,
      data_quality: 0.89,
      api_endpoint: 'https://api.saglik.gov.tr/emergency-services',
      fetch_frequency_hours: 0.5
    },
    {
      id: '7',
      source_name: 'Belediye Güvenlik Kameraları',
      source_type: 'surveillance_network',
      status: 'processing',
      last_fetch: new Date(Date.now() - 1000 * 60 * 5).toISOString(), // 5 dakika önce
      next_fetch: new Date(Date.now() + 1000 * 60 * 25).toISOString(), // 25 dakika sonra
      records_fetched: 1247,
      success_rate: 87.3,
      reliability_score: 0.84,
      data_quality: 0.88,
      api_endpoint: 'https://api.ibb.gov.tr/security-cameras',
      fetch_frequency_hours: 0.5
    },
    {
      id: '8',
      source_name: 'Türk Telekom Güvenlik',
      source_type: 'telecom_security',
      status: 'active',
      last_fetch: new Date(Date.now() - 1000 * 60 * 30).toISOString(), // 30 dakika önce
      next_fetch: new Date(Date.now() + 1000 * 60 * 30).toISOString(), // 30 dakika sonra
      records_fetched: 156,
      success_rate: 95.8,
      reliability_score: 0.90,
      data_quality: 0.92,
      api_endpoint: 'https://api.turktelekom.com.tr/security-intel',
      fetch_frequency_hours: 1
    }
  ]);

  const fetchLearningData = async () => {
    try {
      setRefreshing(true);
      const sources = await aiService.getLearningSource();
      setLearningSources(sources);
      
      notification.success({
        message: '🌐 Internet Learning Verileri Güncellendi',
        description: 'External data sources durumu yüklendi.',
        placement: 'topRight'
      });
    } catch (error) {
      console.error('Learning sources fetch error:', error);
      notification.error({
        message: '❌ Veri Yüklenemedi',
        description: 'Internet learning kaynakları alınamadı.',
        placement: 'topRight'
      });
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchLearningData();
    
    // Auto refresh every 2 minutes
    const interval = setInterval(fetchLearningData, 120000);
    return () => clearInterval(interval);
  }, []);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'green';
      case 'syncing': return 'blue';
      case 'inactive': return 'orange';
      case 'error': return 'red';
      default: return 'default';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'active': return <CheckCircleIcon  />;
      case 'syncing': return <RefreshCwIcon  className="animate-spin" />;
      case 'inactive': return <ClockIcon  />;
      case 'error': return <XCircleIcon  />;
      default: return <AlertTriangleIcon  />;
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'active': return 'Aktif';
      case 'syncing': return 'Senkronize Ediliyor';
      case 'inactive': return 'Pasif';
      case 'error': return 'Hata';
      default: return 'Bilinmiyor';
    }
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'malware_domains': return '🦠';
      case 'threat_intelligence': return '🔍';
      case 'security_research': return '🔬';
      case 'vulnerability_data': return '⚠️';
      default: return '📡';
    }
  };

  const columns = [
    {
      title: 'Kaynak',
      dataIndex: 'source_name',
      key: 'source_name',
      render: (text: string, record: DataSource) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span style={{ fontSize: '20px' }}>{getTypeIcon(record.source_type)}</span>
          <div>
            <div style={{ fontWeight: 600 }}>{text}</div>
            <div style={{ fontSize: '12px', color: '#999' }}>{record.source_type}</div>
          </div>
        </div>
      ),
    },
    {
      title: 'Durum',
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => (
        <Tag color={getStatusColor(status)} icon={getStatusIcon(status)}>
          {getStatusText(status)}
        </Tag>
      ),
    },
    {
      title: 'Veri Kalitesi',
      dataIndex: 'data_quality',
      key: 'data_quality',
      render: (quality: number) => (
        <Progress 
          percent={Math.round(quality * 100)} 
          size="small"
          strokeColor={quality > 0.9 ? '#52c41a' : quality > 0.8 ? '#fa8c16' : '#ff4d4f'}
        />
      ),
    },
    {
      title: 'Başarı Oranı',
      dataIndex: 'success_rate',
      key: 'success_rate',
      render: (rate: number) => (
        <span style={{ 
          color: rate > 95 ? '#52c41a' : rate > 90 ? '#fa8c16' : '#ff4d4f',
          fontWeight: 600 
        }}>
          {rate.toFixed(1)}%
        </span>
      ),
    },
    {
      title: 'Toplanan Veri',
      dataIndex: 'records_fetched',
      key: 'records_fetched',
      render: (count: number) => (
        <span style={{ fontWeight: 600 }}>
          {count.toLocaleString()}
        </span>
      ),
    },
    {
      title: 'Son Güncelleme',
      dataIndex: 'last_fetch',
      key: 'last_fetch',
      render: (date: string) => (
        <div style={{ fontSize: '12px' }}>
          <div>{new Date(date).toLocaleString('tr-TR')}</div>
          <div style={{ color: '#999' }}>
            {Math.round((Date.now() - new Date(date).getTime()) / (1000 * 60))} dakika önce
          </div>
        </div>
      ),
    },
  ];

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <Spin size="large" />
        <p style={{ marginTop: '16px', fontSize: '16px' }}>🌐 Internet Learning Verileri Yükleniyor...</p>
      </div>
    );
  }

  const activeSources = dataSources.filter(s => s.status === 'active').length;
  const errorSources = dataSources.filter(s => s.status === 'error').length;
  const totalRecords = dataSources.reduce((sum, s) => sum + s.records_fetched, 0);
  const avgQuality = dataSources.reduce((sum, s) => sum + s.data_quality, 0) / dataSources.length;

  return (
    <div style={{ padding: '24px' }}>
      {/* Header */}
      <div style={{ marginBottom: '24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <h2 style={{ margin: 0, fontSize: '24px', fontWeight: 600 }}>
            🌐 Internet Learning Monitor
          </h2>
          <p style={{ margin: '4px 0 0 0', color: '#666' }}>
            External data sources durumu ve internet tabanlı öğrenme sistemi
          </p>
        </div>
        <Button 
          icon={<RefreshCwIcon  />}
          onClick={fetchLearningData}
          loading={refreshing}
          type="primary"
        >
          Yenile
        </Button>
      </div>

      {/* Overview Stats */}
      <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Aktif Kaynaklar"
              value={activeSources}
              suffix={`/ ${dataSources.length}`}
              prefix={<GlobeIcon />}
              valueStyle={{ color: '#52c41a' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Toplanan Veri"
              value={totalRecords}
              prefix={<DatabaseIcon />}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Ortalama Kalite"
              value={Math.round(avgQuality * 100)}
              suffix="%"
              prefix={<TrendingUpIcon  />}
              valueStyle={{ color: '#722ed1' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Badge count={errorSources} offset={[10, 0]}>
              <Statistic
                title="Sistem Durumu"
                value={errorSources === 0 ? "Sağlıklı" : "Dikkat"}
                prefix={<ShieldIcon  />}
                valueStyle={{ color: errorSources === 0 ? '#52c41a' : '#fa8c16' }}
              />
            </Badge>
          </Card>
        </Col>
      </Row>

      {/* Status Alert */}
      {errorSources > 0 && (
        <Alert
          message={`⚠️ ${errorSources} kaynak'ta hata var`}
          description="Bazı internet learning kaynakları erişilemez durumda. IT ekibi bilgilendirildi."
          type="warning"
          showIcon
          style={{ marginBottom: '24px' }}
          action={
            <Button size="small" type="text">
              Detayları Gör
            </Button>
          }
        />
      )}

      <Row gutter={[16, 16]}>
        {/* Data Sources Table */}
        <Col xs={24} lg={16}>
          <Card 
            title="📊 Veri Kaynakları Durumu"
            extra={<Tag color="blue">Canlı İzleme</Tag>}
          >
            <Table
              dataSource={dataSources}
              columns={columns}
              rowKey="id"
              pagination={false}
              size="small"
              scroll={{ x: true }}
            />
          </Card>
        </Col>

        {/* Learning Timeline */}
        <Col xs={24} lg={8}>
          <Card 
            title="⏱️ Öğrenme Aktiviteleri"
            extra={<Tag color="green">Real-time</Tag>}
            style={{ height: '500px', overflowY: 'auto' }}
          >
            <Timeline
              items={[
                {
                  color: 'green',
                  children: (
                    <div>
                      <strong>EGM Crime Data Sync</strong>
                      <br />
                      <small style={{ color: '#999' }}>
                        1,247 suç kaydı güncellendi | 15 dakika önce
                      </small>
                    </div>
                  ),
                },
                {
                  color: 'blue',
                  children: (
                    <div>
                      <strong>AFAD Emergency Intelligence</strong>
                      <br />
                      <small style={{ color: '#999' }}>
                        892 acil durum bildirimi işlendi | 30 dakika önce
                      </small>
                    </div>
                  ),
                },
                {
                  color: 'orange',
                  children: (
                    <div>
                      <strong>Municipality Safety Cameras</strong>
                      <br />
                      <small style={{ color: '#999' }}>
                        423 güvenlik kamerası analizi | 45 dakika önce
                      </small>
                    </div>
                  ),
                },
                {
                  color: 'purple',
                  children: (
                    <div>
                      <strong>112 Emergency Call Analytics</strong>
                      <br />
                      <small style={{ color: '#999' }}>
                        156 acil çağrı pattern analizi | 1 saat önce
                      </small>
                    </div>
                  ),
                },
                {
                  color: 'cyan',
                  children: (
                    <div>
                      <strong>Telecom Safety Intelligence</strong>
                      <br />
                      <small style={{ color: '#999' }}>
                        Telekom güvenlik verileri güncellendi | 2 saat önce
                      </small>
                    </div>
                  ),
                },
              ]}
            />

            <div style={{ marginTop: '24px', padding: '16px', backgroundColor: '#f0f8ff', border: '1px solid #91d5ff', borderRadius: '6px' }}>
              <div style={{ fontWeight: 600, color: '#1890ff', marginBottom: '8px' }}>
                🇹🇷 Türkiye Safety Intelligence
              </div>
              <div style={{ fontSize: '14px', marginBottom: '8px' }}>
                <strong>Bugün toplanan güvenlik verileri:</strong>
              </div>
              <div style={{ fontSize: '12px', color: '#666' }}>
                • {totalRecords.toLocaleString()} güvenlik kaydı<br/>
                • {dataSources.filter(s => s.status === 'active').length} aktif güvenlik kaynağı<br/>
                • %{Math.round(avgQuality * 100)} ortalama veri güvenilirliği
              </div>
            </div>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default InternetLearningMonitor;
